import React, { useEffect, useState, memo, useCallback } from 'react'
import { makeStyles } from '@mui/styles'
import { Box, Typography } from '@mui/material'

import CustomButton from 'components/CustomButton'
import { CaseMasterScreens } from './constant'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4)
  },
  cardContainer: {
    display: 'flex',
    gap: theme.spacing(4),
    width: '100%'
  },
  cardRoot: {
    borderRadius: theme.spacing(1),
    boxShadow: `0px 0px 4px 0px #00000026`,
    backgroundColor: theme.palette.shades['clr-white-900'],
    padding: theme.spacing(10, 52.5, 6, 8),
    display: 'flex',
    width: '100%',
    minHeight: '320px',
    height: '100%',
    position: 'relative',
    [theme.breakpoints.down('1500')]: {
      minHeight: '400px'
    }
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
  },
  caseImage: {
    position: 'absolute',
    right: '16px',
    top: '64px',
    width: '164px'
  },
  bannerContainer: {
    position: "relative",
    overflow: "hidden",
    height: "150px",
  },
  commonStyle: {
    display: "flex",
    justifyContent: 'space-between',
    boxShadow: "0px 0px 4px 0px #00000026",
    backgroundColor: theme.palette.shades["clr-white-900"],
    borderRadius: theme.spacing(1),
    position: "absolute",
    width: "100%",
    height: "100%",
    transition: "opacity 0.5s ease-in-out",
  },
  betaContainer: {
    background: "linear-gradient(90deg, #FFFFFF 0%, #E8E4FF 100%)",
    padding: theme.spacing(8, 8, 0, 8),
  },
  comingSoonContainer: {
    background: "linear-gradient(90deg, #FFFFFF 0%, #FFF2DA 75.74%)",
    padding: theme.spacing(8),
  },
  hidden: {
    opacity: 0,
    pointerEvents: "none",
  },
  visible: {
    opacity: 1,
    zIndex: 1,
  },
  textureImage: {
    position: 'absolute',
    left: 0,
    top: 0,
  },
  cardContentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
  },
  contentList: {
    paddingLeft: '1rem',
    margin: '8px 0',
  },
  listItem: {
    marginBottom: '8px',
  },
  bannerContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
}))

// Moved outside component to avoid recreation on each render
const cardData = [
  {
    _id: 'CASE_STUDY',
    heading: 'Prepare Case Studies for Interview Rounds in a Virtual Interview Scenario',
    content: [
      'Choose among Case Types for Practice',
      'Get ready to crack your interview rounds easily'
    ],
    buttonCta: 'Choose Case',
    image: 'https://assets.languify.in/images/dsaBanner.png'
  }
]

export const HeroCards = memo(({ setActiveScreen }) => {
  const classes = useStyles();

  const handleClick = useCallback(() => {
    setActiveScreen(CaseMasterScreens.TOPICS);
  }, [setActiveScreen]);

  return (
    <Box className={classes.cardContainer}>
      {cardData.map((data) => (
        <Box className={classes.cardRoot} key={data._id}>
          <img
            src='https://assets.languify.in/images/gradientBlue.png'
            alt='texture'
            width={300}
            height='100%'
            className={classes.textureImage}
          />
          <Box className={classes.cardContentWrapper}>
            <Box className={classes.cardContent}>
              <Typography variant='h5-semiBold'>{data.heading}</Typography>
              <ul className={classes.contentList}>
                {data.content.map((point, idx) => (
                  <li className={classes.listItem} key={idx}>
                    <Typography variant='body01-bold' color='neutral.clr-700'>
                      {point}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Box>
            <CustomButton
              onClick={handleClick}
              sx={{ width: 'fit-content' }}
              endIcon={
                <img
                  src='https://assets.languify.in/images/arrow-right-white.png'
                  alt='icon'
                  width={20}
                />
              }
            >
              {data.buttonCta}
            </CustomButton>
          </Box>
          <img
            src={data.image}
            alt='illustration'
            className={classes.caseImage}
          />
        </Box>
      ))}
    </Box>
  )
})

const BetaBanner = memo(() => {
  const classes = useStyles();
  const [showBeta, setShowBeta] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowBeta((prev) => !prev);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box className={classes.bannerContainer}>
      {/* Beta Container */}
      <Box
        className={`${classes.commonStyle} ${classes.betaContainer} ${showBeta ? classes.visible : classes.hidden}`}
      >
        <Box className={classes.bannerContent}>
          <img src="https://assets.languify.in/images/betaTag.png" alt="tag" width={53} />
          <Typography variant="h6-medium" color="neutral.clr-800">
            Master case study interviews with business scenarios, problem-solving frameworks, and structured thinking approaches used in consulting and strategic roles.
          </Typography>
        </Box>
        <img src="https://assets.languify.in/images/codingFlow.png" alt="flow" width={410} />
      </Box>

      {/* Coming Soon Container */}
      <Box
        className={`${classes.commonStyle} ${classes.comingSoonContainer} ${showBeta ? classes.hidden : classes.visible}`}
      >
        <Box className={classes.bannerContent}>
          <img src="https://assets.languify.in/images/comingSoonTag.png" alt="tag" width={104} />
          <Typography variant="h6-medium" color="neutral.clr-800">
            Practice with real business cases from top consulting firms - McKinsey, BCG, Bain and more with detailed frameworks.
          </Typography>
        </Box>
        <img src="https://assets.languify.in/images/30Days.png" alt="flow" width={165} />
      </Box>
    </Box>
  );
})

const HeroSection = ({ setActiveScreen }) => {
  const classes = useStyles();
  return (
    <Box className={classes.container}>
      <HeroCards setActiveScreen={setActiveScreen} />
      {/* <BetaBanner /> */}
    </Box>
  )
}

export default HeroSection
